"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.UserSession = void 0;
const api_1 = require("@streem/api");
const sdk_chime_video_1 = require("@streem/sdk-chime-video");
const domain_id_1 = require("@streem/domain-id");
const toolbox_1 = require("@streem/toolbox");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const detect_browser_1 = require("detect-browser");
const streem_error_1 = require("../../streem_error");
const build_room_invitation_1 = require("../../util/build_room_invitation");
const logger_1 = require("../../util/logger");
const filter_null_1 = require("../../util/rx/filter_null");
const first_from_group_1 = require("../../util/rx/first_from_group");
const sdk_debug_1 = __importDefault(require("../../util/toggles/sdk_debug"));
const room_token_controller_1 = require("../controllers/room_token_controller");
const user_wall_1 = require("../wall/user_wall");
const wall_sync_builder_1 = require("../wall/wall_sync_builder");
const detail_session_1 = require("./detail_session");
const host_room_session_1 = require("./host_room_session");
const onsite_room_session_1 = require("./onsite_room_session");
const spectator_room_session_1 = require("./spectator_room_session");
const determine_preferred_video_codecs_1 = require("../../util/determine_preferred_video_codecs");
class UserSession {
    static initialize(config) {
        return __awaiter(this, void 0, void 0, function* () {
            const wallSyncClient = config.wallSyncClient ||
                (yield new wall_sync_builder_1.UserWallSyncBuilder(config.userId, config.deviceId).build());
            return new UserSession(config, wallSyncClient);
        });
    }
    constructor(config, wallSyncClient) {
        this.config = config;
        this.roomSessionSubject = new rxjs_1.BehaviorSubject(undefined);
        this.roomSession = this.roomSessionSubject.asObservable();
        this.clearRoomSession = () => {
            this.roomSessionSubject.next(undefined);
        };
        logger_1.logger.info(`Creating new UserSession for userId=${config.userId}`);
        this.roomTokenController = new room_token_controller_1.RoomTokenController();
        this.userWall = new user_wall_1.UserWall({
            localUserId: this.config.userId,
            localDeviceId: this.config.deviceId,
            wallSyncClient: wallSyncClient,
        });
        this.userWallReconnectFailed = this.userWall.reconnectFailed;
        this.lastIdentifiedDevice = this.userWall.userDeviceUpdates.pipe((0, first_from_group_1.firstFromGroup)(), (0, filter_null_1.filterNull)(), (0, operators_1.distinctUntilChanged)((prev, curr) => (prev === null || prev === void 0 ? void 0 : prev.deviceSid) === (curr === null || curr === void 0 ? void 0 : curr.deviceSid)), (0, operators_1.filter)(device => {
            (0, toolbox_1.invariant)(device.deviceSid, 'Device should always have a sid');
            return !this.config.deviceId.compare(new domain_id_1.DeviceId(device.deviceSid));
        }), (0, operators_1.tap)(device => {
            logger_1.logger.info(`Detected login from another device with sid ${device === null || device === void 0 ? void 0 : device.deviceSid}`);
        }));
        this.currentSmsDeliveryStatus = this.userWall.smsDeliveryStatuses.pipe((0, first_from_group_1.firstFromGroup)(), (0, filter_null_1.filterNull)(), (0, operators_1.distinctUntilChanged)());
        this.groupReservations = this.userWall.groupReservations.pipe((0, operators_1.distinctUntilChanged)());
        this.currentAvailabilityStatus = this.userWall.userAvailabilityStatus.pipe((0, operators_1.distinctUntilChanged)());
        this.currentPendingInvitation = this.userWall.currentPendingInvite.pipe((0, operators_1.withLatestFrom)(this.roomSessionSubject), (0, operators_1.exhaustMap)(([invitation, roomSession]) => {
            if (!invitation) {
                return (0, rxjs_1.of)(undefined);
            }
            // Attempting to rejoin the existing room? Automatically accept
            (0, toolbox_1.invariant)(invitation === null || invitation === void 0 ? void 0 : invitation.roomSid, 'Invitation must have a roomSid');
            const roomIdMatchesCurrentRoom = invitation &&
                roomSession &&
                roomSession.roomId.compare(new domain_id_1.RoomId(invitation.roomSid));
            if (roomIdMatchesCurrentRoom) {
                return this.acceptRoomInvitationAndRejoinRoom(invitation);
            }
            // This is a new invite. Let's fetch info about the user
            (0, toolbox_1.invariant)(invitation.fromUserSid, 'Pending invitation must have fromUserSid');
            return this.getUserAndBuildInvite(invitation);
        }));
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.userWall.connect();
        });
    }
    disconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.userWall.disconnect();
            this.roomTokenController.disconnect();
        });
    }
    /**
     * Get the SMS delivery status of an invitation by invitation sid
     *
     * NOTE 2022-05-18 Taylor: I don't think this function is working entirely as intended.
     * The only time this pipe will emit is if the last changed sms delivery status happens to be the id we're looking for,
     * which can be missed if events fire quickly.
     */
    getSmsInviteStatusBySid(sid) {
        return this.currentSmsDeliveryStatus.pipe((0, operators_1.filter)(status => (status === null || status === void 0 ? void 0 : status.invitationSid) === sid), (0, operators_1.tap)(status => {
            if (!status)
                return;
            logger_1.logger.info(`SMS delivery status changed for invitation '${status.invitationSid}', message: '${status.messageId}'. New status: '${status.deliveryStatus}'`);
        }));
    }
    acceptInvitation(invitation, callConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            logger_1.logger.info('Invitation received, creating SpectatorRoomSession');
            if (sdk_debug_1.default.allowFaceToFaceMode) {
                callConfig.faceToFaceModeEnabled = true;
            }
            logger_1.logger.info('Attempting to use invitation code: ', invitation.id.toString());
            try {
                yield api_1.StreemAPI.rooms.acceptRoomInvitation(invitation.roomId.toString(), invitation.id.toString(), {
                    acceptedByUserSid: this.config.userId.toString(),
                });
                logger_1.logger.info('Invitation has been accepted');
            }
            catch (error) {
                logger_1.logger.info('Invitation could not be accepted: ', error);
                throw new streem_error_1.StreemError('Unable to accept invitation. Could not accept invite.');
            }
            yield this.roomTokenController.fetchRoomVideoCredentials(invitation.roomId, this.config.userId);
            if (!this.roomTokenController.hasToken()) {
                throw new streem_error_1.StreemError('Unable to accept invitation. No room video credentials found.');
            }
            const sessionConfig = {
                onEnd: this.clearRoomSession,
                localUserId: this.config.userId,
                localDeviceId: this.config.deviceId,
                remoteUserId: invitation.fromUserId,
                remoteDeviceId: invitation.fromDeviceId,
                roomId: invitation.roomId,
                callSettings: this.getCallSettings(callConfig),
            };
            const spectatorRoomSession = yield spectator_room_session_1.SpectatorRoomSession.initialize(sessionConfig);
            try {
                yield spectatorRoomSession.connectToRoom();
            }
            catch (error) {
                yield spectatorRoomSession.leaveRoom();
                const connectionError = new streem_error_1.StreemError('Spectator failed to connect to room after accepting invitation');
                connectionError.cause = error;
                throw connectionError;
            }
            this.roomSessionSubject.next(spectatorRoomSession);
            return spectatorRoomSession;
        });
    }
    rejectInvitation(invitation) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield api_1.StreemAPI.rooms.rejectRoomInvitation(invitation.roomId.toString(), invitation.id.toString(), {
                    rejectedByUserSid: this.config.userId.toString(),
                });
                logger_1.logger.info(`${this.config.userId} has rejected invitation ${invitation.id} for room ${invitation.roomId}`);
            }
            catch (error) {
                logger_1.logger.error(`Failed to reject invitation ${invitation.id}: `, error);
                throw error;
            }
        });
    }
    startOnSiteStreem(onsiteConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const room = yield this.createOrGetRoom(onsiteConfig);
                if (!room || !room.sid) {
                    throw new streem_error_1.StreemError('Failed to create room for remote streem.');
                }
                const roomId = new domain_id_1.RoomId(room.sid);
                yield this.roomTokenController.fetchRoomVideoCredentials(roomId, this.config.userId);
                if (!this.roomTokenController.hasToken()) {
                    throw new streem_error_1.StreemError('Failed to start on site streem. No room video credentials found');
                }
                logger_1.logger.debug('Starting OnSiteStreem');
                const roomSession = yield onsite_room_session_1.OnSiteRoomSession.initialize({
                    onEnd: this.clearRoomSession,
                    localUserId: this.config.userId,
                    localDeviceId: this.config.deviceId,
                    remoteUserId: this.config.userId,
                    remoteDeviceId: this.config.deviceId,
                    roomId: roomId,
                    callSettings: this.getCallSettings(Object.assign(Object.assign({}, onsiteConfig.callConfig), { faceToFaceModeEnabled: false })),
                });
                this.roomSessionSubject.next(roomSession);
                yield roomSession.startOnSiteCall();
                return roomSession;
            }
            catch (error) {
                logger_1.logger.error('startOnsiteStreem failed', error);
                this.clearRoomSession();
                throw new streem_error_1.StreemError(`Internal Error: ${error}`);
            }
        });
    }
    startRemoteStreem(remoteStreemConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            logger_1.logger.info('startRemoteStreem has been called');
            try {
                // This is only for twilio. If we don't send a preferred codec, the backend will default to h264
                // when creating the twilio room. We can't prevent sending a preferred codec for chime since
                // we first have to call createOrGetRoom, which will then les us know if we are using twilio or chime.
                const preferredVideoCodec = remoteStreemConfig.preferredCodec ||
                    (yield (0, determine_preferred_video_codecs_1.determinePreferredVideoCodec)(window.navigator.userAgent, (0, detect_browser_1.detect)()));
                let mediaRegion = undefined;
                logger_1.logger.info(`Attempting to createOrGetRoom with userId=${this.config.userId} deviceId=${this.config.deviceId}`);
                const room = yield this.createOrGetRoom(Object.assign(Object.assign({}, remoteStreemConfig), { preferredCodec: preferredVideoCodec }));
                (0, toolbox_1.invariant)(room, 'Room is required to create a room session');
                const roomId = new domain_id_1.RoomId(room.sid);
                if (room.videoPlatform === api_1.StreemApiRoomVideoPlatform.CHIME) {
                    mediaRegion = yield (0, sdk_chime_video_1.fetchClosestMediaRegion)();
                    logger_1.logger.info(`Closest region is ${mediaRegion}`);
                }
                yield this.roomTokenController.fetchRoomVideoCredentials(roomId, this.config.userId, mediaRegion);
                if (!this.roomTokenController.hasToken()) {
                    throw new streem_error_1.StreemError('Failed to start remote streem. No room video credentials found');
                }
                // Create non expert session
                const sessionConfig = {
                    onEnd: this.clearRoomSession,
                    localUserId: this.config.userId,
                    localDeviceId: this.config.deviceId,
                    remoteDeviceId: remoteStreemConfig.withDeviceId,
                    remoteUserId: remoteStreemConfig.withUserId,
                    roomId: roomId,
                    callSettings: this.getCallSettings(remoteStreemConfig.callConfig),
                    isHostSession: true,
                };
                logger_1.logger.info(`Starting a remote streem for roomId=${roomId} localUserId=${this.config.userId} deviceId=${this.config.deviceId}`);
                const hostRoomSession = yield host_room_session_1.HostRoomSession.initialize(sessionConfig);
                this.roomSessionSubject.next(hostRoomSession);
                return hostRoomSession;
            }
            catch (error) {
                logger_1.logger.error('startRemoteStreem failed', error);
                this.clearRoomSession();
                if (error instanceof api_1.APIError) {
                    throw new api_1.APIError('API Error', error.response);
                }
                else {
                    throw new streem_error_1.StreemError(`Internal Error: ${error}`);
                }
            }
        });
    }
    startDetailSession(roomSid) {
        return __awaiter(this, void 0, void 0, function* () {
            const detailSession = yield detail_session_1.DetailSession.initialize({
                userId: this.config.userId,
                deviceId: this.config.deviceId,
                roomId: new domain_id_1.RoomId(roomSid),
                readOnly: false,
            });
            yield detailSession.connect();
            return detailSession;
        });
    }
    createOrGetRoom(config) {
        return __awaiter(this, void 0, void 0, function* () {
            if (config.roomId) {
                try {
                    const { room } = yield api_1.StreemAPI.rooms.getRoom(config.roomId);
                    return room;
                }
                catch (error) {
                    throw new streem_error_1.StreemError(`Failed to getRoom for roomId=${config.roomId} userId=${this.config.userId} deviceId=${this.config.deviceId} error=${error}`);
                }
            }
            else {
                try {
                    const { room } = yield api_1.StreemAPI.rooms.createRoom({
                        createdByUserSid: this.config.userId.toString(),
                        referenceId: config.referenceId,
                        integrationId: config.integrationId,
                        preferredCodec: config.preferredCodec,
                    });
                    return room;
                }
                catch (error) {
                    throw new streem_error_1.StreemError(`Failed to createRoom for userId=${this.config.userId} deviceId=${this.config.deviceId} error=${error}`);
                }
            }
        });
    }
    getUserAndBuildInvite(invitation) {
        (0, toolbox_1.invariant)(invitation.fromUserSid, 'Pending invitation must have fromUserSid');
        return (0, rxjs_1.from)(api_1.StreemAPI.users.getUser(invitation.fromUserSid)).pipe((0, operators_1.map)(userResponse => {
            (0, toolbox_1.invariant)(userResponse.user, 'User is required to build an room invite data');
            return (0, build_room_invitation_1.buildRoomInviteData)(invitation, userResponse.user);
        }), (0, operators_1.catchError)(error => {
            logger_1.logger.error(`Attempted to find user ${invitation.fromUserSid} from invitation ${invitation.sid} and failed.`, error);
            return (0, rxjs_1.of)(undefined);
        }));
    }
    acceptRoomInvitationAndRejoinRoom(invitation) {
        (0, toolbox_1.invariant)(invitation === null || invitation === void 0 ? void 0 : invitation.roomSid, 'Invitation must have a roomSid');
        logger_1.logger.info('Attempting to re-join existing room, accepting new invite.', invitation.id);
        api_1.StreemAPI.rooms.acceptRoomInvitation(invitation.roomSid, invitation.id, {
            acceptedByUserSid: this.config.userId.toString(),
        });
        return (0, rxjs_1.of)(undefined);
    }
    getCallSettings(callConfig) {
        const credentialsAndPlatform = this.roomTokenController.roomVideoCredentialsAndPlatform;
        return Object.assign(Object.assign({}, callConfig), credentialsAndPlatform);
    }
}
exports.UserSession = UserSession;
//# sourceMappingURL=user_session.js.map